import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, Outlet } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import Unauthorize from '../assets/Unauthorize.jpg'
import { userProfile } from '../features/auth/authAction'
import { useParams } from 'react-router-dom'
import { getFavourationList, fetchUserPermission } from '../features/auth/authAction'
import { getPaidList } from '../features/cartAction';
import Cookies from 'js-cookie'
import getUserToken from '../helpers/tokenValidation'

const labels = {
  si: {
    title: 'අනවසර',
    message: 'මෙම පිටුවට ප්‍රවේශ වීමට ඔබට අවශ්‍ය වන්නේ ලියාපදිංචි වීමයි',
    createAccount: 'ගිණුමක් සාදන්න',
    alreadyHaveAccount: 'දැනටමත් ගිණුමක් ඇත? ඇතුල් වන්න',
  },
  en: {
    title: 'Unauthorized',
    message: 'You need to register to access this page',
    createAccount: 'Create an Account',
    alreadyHaveAccount: 'Already have an account? Log in',
  }
};

const ProtectedRoute = () => {
  const dispatch = useDispatch();
  const { loggedUId } = useParams();
  const loggedDetails = useSelector((state) => state.auth.profileInfo?.user)
  const [lang, setLang] = useState(() => localStorage.getItem('language') || 'si');

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'language' && e.newValue) setLang(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    const interval = setInterval(() => {
      const currentLang = localStorage.getItem('language') || 'si';
      setLang(prevLang => prevLang !== currentLang ? currentLang : prevLang);
    }, 500);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  const t = labels[lang] ?? labels.si;

  Cookies.set('user_id', loggedUId, { expires: 1 });

  useEffect(() => {
    // Check if token is valid before fetching profile
    const token = getUserToken();

    // Only fetch profile if we have a valid token and don't have profile data
    if (token && !loggedDetails) {
      dispatch(userProfile())
    }

    // Use loggedDetails.id or fallback to localStorage
    const currentUserId = loggedDetails?.id || localStorage.getItem('user_id');

    if (currentUserId && token) {
      dispatch(getFavourationList(currentUserId));
      dispatch(getPaidList(currentUserId))
      dispatch(fetchUserPermission(currentUserId))

      // Ensure these are set if they aren't already
      if (!localStorage.getItem('user_id')) {
        localStorage.setItem('user_id', currentUserId);
      }
      if (loggedDetails?.detailId) {
        localStorage.setItem('detailId', loggedDetails.detailId);
      }
    }
  }, [])


  // Check authentication
  const isAuthenticated = !!loggedDetails;

  // Check if route requires a specific user ID match
  const requiresUserMatch = !!loggedUId;

  // 1. Authorized if logged in and no specific user ID required (e.g. /profile)
  if (isAuthenticated && !requiresUserMatch) {
    return <Outlet />
  }

  // 2. Authorized if logged in and user ID matches
  if (isAuthenticated && requiresUserMatch && Number(loggedUId) === loggedDetails?.id) {
    return <Outlet />
  }

  // Otherwise, show unauthorized UI
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        px: 3,
        py: 6,
      }}
    >
      <Box
        sx={{
          maxWidth: 600,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: 4,
          p: 4,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            fontSize: '4rem',
            mb: 2,
            color: 'warning.main',
          }}
        >
          ⚠️
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 2,
            background: 'linear-gradient(135deg, #71123e 0%, #8b1a4f 50%, #C3937C 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'text.primary',
          }}
        >
          {t.title}
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
          {t.message}
        </Typography>

        <Box
          component="img"
          src={Unauthorize}
          alt="Unauthorized"
          sx={{
            width: '100%',
            maxWidth: 400,
            height: 'auto',
            borderRadius: 3,
            mb: 4,
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <Box
            component={NavLink}
            to="/signUp"
            sx={{
              textDecoration: 'none',
              width: '100%',
              maxWidth: 300,
            }}
          >
            <Box
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                px: 4,
                py: 2,
                borderRadius: 3,
                fontWeight: 600,
                fontSize: '1.1rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(113, 18, 62, 0.3)',
                },
              }}
            >
              {t.createAccount}
            </Box>
          </Box>

          <Box
            component={NavLink}
            to="/login"
            sx={{
              textDecoration: 'none',
              width: '100%',
              maxWidth: 300,
            }}
          >
            <Box
              sx={{
                bgcolor: 'background.paper',
                color: 'primary.main',
                border: '2px solid',
                borderColor: 'primary.main',
                px: 4,
                py: 2,
                borderRadius: 3,
                fontWeight: 600,
                fontSize: '1.1rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(113, 18, 62, 0.3)',
                },
              }}
            >
              {t.alreadyHaveAccount}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
export default ProtectedRoute